/**
 * Declarative interface for Viewer3D.
 */

import type { Resource, ResourceLoader } from './resource-loader';

import { VCoord, VCamera, Viewer3DContent, VScene, VItem, VColor, VItemContainer, VGroup, VPart, VLocation, VPartPositioning, VMeshRule, VMeshRuleLoaded, VMaterialLoaded, VLocationLoaded, VRotation, VScale, VGroupPosition, VGroupSpatialInfo, VGroupAttributes, VThumbnailInfo } from './viewer-3d-content';
import { V3BDefaultQualitySettings, Viewer3DBabylon } from './viewer-3d-babylon';
import { diff } from 'deep-object-diff';

const MODEL_EXTENSIONS = [ 'glb', 'gltf' ];
const TEXTURE_EXTENSIONS = [ 'webp', 'png', 'jpg' ];
const AOMAP_EXTENSIONS = ['jpg', 'png' ];
const ENVIRONMENT_EXTENSIONS = [ 'env', 'dds' ];


/**
 * 
 */
export class Viewer3DSync {

    private viewer: Viewer3DBabylon;
    private prevContent: Viewer3DContent;
    private loader: ResourceLoader;
    private currentSync: Promise<void>;
    private loadingListeners: ((loading: boolean) => void)[];
    private debug: boolean;

    constructor(viewer: Viewer3DBabylon, loader: ResourceLoader, debug: boolean = false) {
        this.viewer = viewer;
        this.prevContent = {
            qualitySettings: V3BDefaultQualitySettings,
            scenes: {},
            watchedNodes: []
        }
        this.loader = loader;
        this.currentSync = Promise.resolve();
        this.loadingListeners = [];
        this.debug = debug;
    }

    async sync(newc: Viewer3DContent): Promise<void> {
        this.currentSync = this.currentSync.then(() => this.syncAux(newc));
        return this.currentSync;
    }

    addLoadingListener(listener: (loading: boolean) => void): void {
        this.loadingListeners.push(listener);
    }

    private async syncAux(newc: Viewer3DContent): Promise<void> {
        try {
            const actions = this.makeActions(newc);
            const [ preloadPromises, needsLoading ] = this.preload(actions);
            if(needsLoading) {
                this.loadingListeners.forEach(l => l(true));
            }
            let syncActions = await Promise.all(preloadPromises);
            this.viewer.startUpdate();
            for(const action of syncActions) {
                if(this.debug) {
                    console.log(action);
                }
                await this.execute(action);
            }
            this.viewer.endUpdate();
            this.loadingListeners.forEach(l => l(false));
            this.prevContent = newc;
        } catch(err) {
            console.error(err);
            this.viewer.endUpdate();
        }
    }

    private preload(actions: Action[]): [ Promise<ResolvedAction>[], boolean ] {

        const rsActions: Promise<ResolvedAction>[] = [];
        let addLayout: Set<string> = new Set();
        let needsLoading = false;

        for(const a of actions) {
            try {
                switch(a.action) {
                    case 'part-create':
                        rsActions.push((async () => {
                            const model = await this.loadAsBlob(a.part.model, MODEL_EXTENSIONS);
                            return <PreloadedAction>{
                                action: 'part-create-s',
                                sceneId: a.sceneId,
                                parentId: a.parentId,
                                partId: a.part.id,
                                model,
                                position: a.part.position
                            };
                        })());
                        needsLoading = true;
                        break;

                    case 'part-mesh-rules':
                        rsActions.push((async () => {
                            const rules = await Promise.all(a.rules.map(r => this.preloadMeshRule(r)));
                            return <PreloadedAction>{
                                action: 'part-mesh-rules-s',
                                sceneId: a.sceneId,
                                itemId: a.itemId,
                                rules
                            }
                        })());
                        needsLoading = true;
                        break;

                    case 'environment':
                        rsActions.push((async () => {
                            const location = await this.loadAsBlob(a.location, ENVIRONMENT_EXTENSIONS);
                            return <PreloadedAction>{
                                action: 'environment-s',
                                sceneId: a.sceneId,
                                location
                            }
                        })());
                        needsLoading = true;
                        break;

                    case 'layout':
                        addLayout.add(a.sceneId);
                        break;

                    default:
                        rsActions.push( Promise.resolve(a) );
                        break;
                }
            } catch(err) {
                console.error('Failed to resolve action:', a);
                console.error(err);
            }
        }

        for(let sceneId of Array.from(addLayout.values())) {
            rsActions.push(Promise.resolve(<ResolvedAction>{action: 'layout', sceneId }));
        }

        return [ rsActions, needsLoading ];
    }

    private async preloadMeshRule(rule: VMeshRule): Promise<VMeshRuleLoaded> {
        try {
            let material: VMaterialLoaded | undefined;

            // TODO optimize loading aoMap and textures in parallel
            let aoMap = await this.loadAsBlob(rule.aoMap, AOMAP_EXTENSIONS);

            if(rule.material) {
                const m = rule.material;
                const [ baseAndAlpha, metalnessAndRoughness, normalAndHeight ] = await Promise.all([
                    this.loadAsBlob(m.baseAndAlpha, TEXTURE_EXTENSIONS),
                    this.loadAsBlob(m.metalnessAndRoughness, TEXTURE_EXTENSIONS),
                    this.loadAsBlob(m.normalAndHeight, TEXTURE_EXTENSIONS),                    
                ]);
    
                material = {
                    name: m.name,
                    alpha: m.alpha,
                    basecolor: m.basecolor,
                    anisotropy: m.anisotropy,
                    clearcoat: m.clearcoat,
                    displacement: m.displacement,
                    ior: m.ior,
                    metalness: m.metalness,
                    metallicReflectanceColor: m.metallicReflectanceColor,
                    roughness: m.roughness,
                    sheen: m.sheen,
                    specular: m.specular,
    
                    baseAndAlpha,
                    metalnessAndRoughness,
                    normalAndHeight
                }
            }
    
            return {
                test: rule.test,
                hide: rule.hide,
                textureScale: rule.textureScale,
                isIcon: rule.isIcon,
                clickable: rule.clickable,
                ignoreInLayout: rule.ignoreInLayout,
                data: rule.data,
                material,
                aoMap
            }
        } catch(err) {
            console.error(err);
            return {
                test: '***ERROR***',
                textureScale: rule.textureScale,
                isIcon: rule.isIcon,
                hide: false
            }
        }

    }

    private async execute(action: ResolvedAction): Promise<void> {

        switch(action.action) {

            case 'quality-settings':
                this.viewer.setQualitySettings(action.settings);
                break;

            case 'bg-color':
                this.viewer.setBackgroundColor(action.sceneId, action.color);
                break;

            case 'scene-create':
                this.viewer.createScene(action.sceneId);
                break;
    
            case 'scene-delete':
                this.viewer.deleteScene(action.sceneId);
                break;

            case 'camera':
                this.viewer.setCamera(action.sceneId, action.camera);
                break;

            case 'thumbnail-view':
                this.viewer.setThumbnailView(action.sceneId, action.info);
                break;

            case 'item-delete':
                this.viewer.deleteItem(action.sceneId, action.itemId);
                break;

            case 'group-create':
                this.viewer.createGroup(action.sceneId, action.parentId, action.itemId);
                break;

            case 'group-position':
                this.viewer.setGroupPosition(action.sceneId, action.itemId, action.position);
                break;

            case 'group-attributes':
                this.viewer.setGroupAttributes(action.sceneId, action.itemId, action.attributes);
                break;
    
            case 'part-create-s':
                await this.viewer.createPart(action.sceneId, action.parentId, action.partId, action.model);
                break;

            case 'part-position':
                this.viewer.setPartPosition(action.sceneId, action.itemId, action.position, action.rotation, action.scale);
                break;

            case 'part-animation':
                this.viewer.setPartAnimation(action.sceneId, action.itemId, action.percentage, action.fixed);
                break;

            case 'part-mesh-rules-s':
                await this.viewer.setPartMeshRules(action.sceneId, action.itemId, action.rules);
                break;
    
            case 'light-hs':
                this.viewer.setHemisphereLight(action.sceneId, action.on);
                break;

            case 'directional-light':
                this.viewer.setDirectionalLight(action.sceneId, action.on);
                break;

            case 'global-dimensions':
                this.viewer.setShowGlobalDimensions(action.sceneId, action.on);
                break;

            case 'shadow-catcher':
                this.viewer.setShadowCatcher(action.sceneId, action.on);
                break;

            case 'clear-scene':
                this.viewer.clearScene(action.sceneId);
                break;
    
            case 'layout':
                this.viewer.layout(action.sceneId);
                break;

            case 'environment-s':
                await this.viewer.setEnvironment(action.sceneId, action.location);
                break;

            case 'watch-nodes':
                this.viewer.watchNodeDimensions(action.watchedNodes);
                break;

            default:
                console.error(action);
                break;

        }

    }

    private makeActions(newc: Viewer3DContent): Action[] {

        const actions: Action[] = [];
        const prevc = this.prevContent;

        if(prevc.qualitySettings !== newc.qualitySettings) {
            actions.push({
                action: 'quality-settings',
                settings: newc.qualitySettings
            })
        }

        let scenesToRemove = Object.getOwnPropertyNames(prevc.scenes);
        
        for(const sceneId of Object.getOwnPropertyNames(newc.scenes)) {
            const newScene = newc.scenes[sceneId];
            const oldScene = prevc.scenes[sceneId];
            if(oldScene) {
                scenesToRemove = scenesToRemove.filter(id => id !== sceneId);
                this.syncScene(actions, sceneId, oldScene, newScene);
            } else {
                this.createScene(actions, sceneId, newScene);

            }
        }

        for(const sceneId of scenesToRemove) {
            actions.push({ action: 'scene-delete', sceneId });
        }

        if('w' in diff({w: prevc.watchedNodes }, { w: newc.watchedNodes})) {
            actions.push({ action: 'watch-nodes', watchedNodes: newc.watchedNodes })
        }

        return actions;

    }

    private createScene(actions: Action[], sceneId: string, scene: VScene) {
        actions.push({
            action: 'scene-create',
            sceneId
        });
        actions.push({
            action: 'bg-color',
            sceneId,
            color: scene.content.backgroundColor
        });
        actions.push({
            action: 'camera',
            sceneId,
            camera: scene.camera
        });
        if(scene.thumbnailView) {
            actions.push({
                action: 'thumbnail-view',
                sceneId,
                info: scene.thumbnailView
            });
        }
        if(scene.content.lights.hemisphere) {
            actions.push({
                action: 'light-hs',
                sceneId,
                on: true
            });
        }
        if(scene.content.directionalShadow) {
            actions.push({
                action: 'directional-light',
                sceneId,
                on: true
            })
        }
        if(scene.content.shadowCatcher) {
            actions.push({
                action: 'shadow-catcher',
                sceneId,
                on: true
            })
        }
        if(scene.content.environment) {
            actions.push({
                action: 'environment',
                sceneId,
                location: scene.content.environment
            })
        }
        if(scene.content.globalDimensions) {
            actions.push({
                action: 'global-dimensions',
                sceneId,
                on: true
            });
        }
        this.addToItemContainer(actions, sceneId, undefined, scene.content.items);
    }

    private syncScene(actions: Action[],sceneId: string, olds: VScene, news: VScene) {

        if(!colorEquals(olds.content.backgroundColor, news.content.backgroundColor)) {
            actions.push({
                action: 'bg-color',
                sceneId,
                color: news.content.backgroundColor
            });
        }

        if(!cameraEquals(olds.camera, news.camera)) {
            actions.push({
                action: 'camera',
                sceneId,
                camera: news.camera
            });
        }

        if(!deepEquals(olds.thumbnailView, news.thumbnailView)) {
            actions.push({
                action: 'thumbnail-view',
                sceneId,
                info: news.thumbnailView
            });
        }

        if(olds.content.lights.hemisphere != news.content.lights.hemisphere) {
            actions.push({
                action: 'light-hs',
                sceneId,
                on: news.content.lights.hemisphere
            });
        }

        if(olds.content.directionalShadow != news.content.directionalShadow) {
            actions.push({
                action: 'directional-light',
                sceneId,
                on: news.content.directionalShadow
            });
        }

        if(olds.content.shadowCatcher != news.content.shadowCatcher) {
            actions.push({
                action: 'shadow-catcher',
                sceneId,
                on: news.content.shadowCatcher
            });
        }

        if(!deepEquals(olds.content.environment, news.content.environment)) {
            actions.push({
                action: 'environment',
                sceneId,
                location: news.content.environment
            });
        }

        if(olds.content.globalDimensions != news.content.globalDimensions) {
            actions.push({
                action: 'global-dimensions',
                sceneId,
                on: news.content.globalDimensions
            });
        }
        
        this.syncItemContainer(actions, sceneId, undefined, olds.content, news.content);

    }

    private addToItemContainer(actions: Action[], sceneId: string, containerId: string|undefined, items: VItem[]) {
        for(const item of items) {
            this.addItem(actions, sceneId, containerId, item);
        }
    }

    private syncItemContainer(actions: Action[], sceneId: string, containerId: string|undefined, 
                              oldic: VItemContainer, newic: VItemContainer) {

        let itemsToRemove = oldic.items.concat([]);

        for(const newItem of newic.items) {
            const oldIndex = itemsToRemove.findIndex(i => i.id === newItem.id);
            if(oldIndex !== -1) {
                const oldItem = itemsToRemove[oldIndex];
                itemsToRemove.splice(oldIndex, 1);
                this.syncItem(actions, sceneId, containerId, oldItem, newItem);
            } else {
                this.addItem(actions, sceneId, containerId, newItem);
            }
        }

        for(const item of itemsToRemove) {
            actions.push({
                action: 'item-delete',
                sceneId, itemId: item.id
            });
        }

    }

    private addItem(actions: Action[], sceneId: string, parentId: string|undefined, item: VItem) {
        if(itemIsGroup(item)) {
            this.addGroup(actions, sceneId, parentId, item)
        } else {
            this.addPart(actions, sceneId, parentId, item);
        }
    }

    private addGroup(actions: Action[], sceneId: string, parentId: string|undefined, group: VGroup) {
        actions.push({
            action: 'group-create',
            sceneId, parentId,
            itemId: group.id
        });
        actions.push({
            action: 'group-attributes',
            sceneId, itemId: group.id,
            attributes: group.attributes
        });
        actions.push({
            action: 'group-position',
            sceneId, itemId: group.id,
            position: group.position
        });
        this.addToItemContainer(actions, sceneId, group.id, group.items);
    }

    private addPart(actions: Action[], sceneId: string, parentId: string|undefined, part: VPart) {
        actions.push({
            action: 'part-create',
            sceneId, parentId, part
        });
        actions.push({
            action: 'part-position',
            sceneId, 
            itemId: part.id,
            position: part.position,
            rotation: part.rotation,
            scale: part.scale
        });
        actions.push({
            action: 'part-mesh-rules',
            sceneId,
            itemId: part.id,
            rules: part.meshes
        });
        actions.push({
            action: 'part-animation',
            sceneId,
            itemId: part.id,
            percentage: part.animationPercentage,
            fixed: part.animationFixed
        });
        actions.push({
            action: 'layout',
            sceneId
        });
    }

    private syncItem(actions: Action[], sceneId: string, parentId: string|undefined, oldItem: VItem, newItem: VItem) {

        let oldIsGroup = itemIsGroup(oldItem);
        let newIsGroup = itemIsGroup(newItem);

        if(oldIsGroup !== newIsGroup) {
            actions.push({
                action: 'item-delete',
                sceneId, itemId: oldItem.id
            });
            this.addItem(actions, sceneId, parentId, newItem);
        } else if(oldIsGroup) {
            this.syncGroup(actions, sceneId, parentId, oldItem as VGroup, newItem as VGroup);
        } else {
            this.syncPart(actions, sceneId, parentId, oldItem as VPart, newItem as VPart);
        }
    }

    private syncGroup(actions: Action[], sceneId: string, parentId: string | undefined, oldItem: VGroup, newItem: VGroup) {
        
        const newActions: Action[] = [];
        this.syncGroupPosition(newActions, sceneId, oldItem.id, oldItem.position, newItem.position);
        this.syncGroupAttributes(newActions, sceneId, oldItem.id, oldItem.attributes, newItem.attributes);
        this.syncItemContainer(newActions, sceneId, oldItem.id, oldItem, newItem);
        actions.push(...newActions);

    }

    private syncGroupPosition(actions: Action[], sceneId: string, itemId: string, oldPosition: VGroupSpatialInfo, newPosition: VGroupSpatialInfo) {
        if(!deepEquals(oldPosition, newPosition)) {
            actions.push({
                action: 'group-position',
                sceneId, itemId,
                position: newPosition
            });
            actions.push({
                action: 'layout',
                sceneId
            });
        }
    }

    private syncGroupAttributes(actions: Action[], sceneId: string, itemId: string, oldAttributes: VGroupAttributes, newAttributes: VGroupAttributes) {
        if(!deepEquals(oldAttributes, newAttributes)) {
            actions.push({
                action: 'group-attributes',
                sceneId, itemId,
                attributes: newAttributes
            });
        }
    }

    private syncPart(actions: Action[], sceneId: string, parentId: string | undefined, oldItem: VPart, newItem: VPart) {
        if(!locationEquals(oldItem.model, newItem.model)) {
            actions.push({
                action: 'item-delete',
                sceneId,
                itemId: oldItem.id
            });
            this.addPart(actions, sceneId, parentId, newItem);
        } else {
            this.syncPartPosition(actions, sceneId, oldItem, newItem);
            this.syncPartMeshRules(actions, sceneId, newItem.id, oldItem.meshes, newItem.meshes);
            this.syncPartAnimation(actions, sceneId, newItem.id, oldItem.animationPercentage, newItem.animationPercentage, oldItem.animationFixed, newItem.animationFixed);
        }
    }

    private syncPartPosition(actions: Action[], sceneId: string, oldItem: VPart, newItem: VPart) {
        if(!deepEquals({
            position: oldItem.position,
            rotation: oldItem.rotation,
            scale: oldItem.scale
        }, {
            position: newItem.position,
            rotation: newItem.rotation,
            scale: newItem.scale
        })) {
            actions.push({
                action: 'part-position',
                sceneId,
                itemId: newItem.id,
                position: newItem.position,
                rotation: newItem.rotation,
                scale: newItem.scale
            });
            actions.push({
                action: 'layout',
                sceneId
            });
        }
    }

    private syncPartMeshRules(actions: Action[], sceneId: string, itemId: string, oldRules: VMeshRule[], newRules: VMeshRule[]) {
        if(!deepEquals(oldRules, newRules)) {
            actions.push({
                action: 'part-mesh-rules',
                sceneId,
                itemId,
                rules: newRules
            });
            actions.push({
                action: 'layout',
                sceneId
            });
        }
    }

    private syncPartAnimation(actions: Action[], sceneId: string, itemId: string, oldPercentage: number|undefined, newPercentage: number|undefined, oldFixed: boolean, newFixed: boolean) {
        if(oldPercentage !== newPercentage || oldFixed !== newFixed) {
            actions.push({
                action: 'part-animation',
                sceneId,
                itemId,
                percentage: newPercentage,
                fixed: newFixed
            });
        }
    }

    private async loadAsBlob(location: VLocation|undefined, extensions: string[]): Promise<VLocationLoaded|undefined> {
        if(location === undefined) {
            return undefined;
        } else {
            let [ blob, ext ] = await this.loader.loadAsBlob(loc2res(location, extensions));
            return { url: URL.createObjectURL(blob), ext };
        }
    }

}

// ==== helpers ====

function cameraEquals(a: VCamera|undefined, b: VCamera|undefined) {
    if(a == b) {
        return true;
    } else {
        return coordEquals(a?.position, b?.position) && coordEquals(a?.target, b?.target);
    }
}

function coordEquals(a:VCoord|undefined, b:VCoord|undefined): boolean {
    if(a == b) {
        return true;
    } else if(a !== undefined && b !== undefined) {
        return a[0] === b[0] && a[1] === b[1] && a[2] === b[2];
    } else {
        return false;
    }
}

function colorEquals(a:VColor|undefined, b:VColor|undefined): boolean {
    if(a == b) {
        return true;
    } else if(a !== undefined && b !== undefined) {
        const nA = normalizeColor(a);
        const nB = normalizeColor(b);
        return nA[0] === nB[0] && nA[1] === nB[1] && nA[2] === nB[2] && nA[3] === nB[3];
    } else {
        return false;    
    }
}

function locationEquals(a: VLocation, b: VLocation): boolean {
    if(a === b) {
        return true;
    } else if(typeof a !== typeof b) {
        return false;
    } else if(typeof a === 'string' || typeof b === 'string') {
        // yeah, the typeof b === 'string' isn't needed, but tell it to the ts 4.9 type checker...
        return a === b;
    } else {
        return a.package === b.package && a.filename === b.filename;
    }
}

function normalizeColor(color: VColor): [ number, number, number, number ] {
    if(color.length === 3) {
        return [...color, 0 ];
    } else {
        return color;
    }
}

function itemIsGroup(item: VItem): item is VGroup {
    return 'items' in item;
}

function loc2res(loc: VLocation, extensions: string[]): Resource {
    if(typeof loc === 'string') {
        return {
            name: loc,
            exts: extensions
        }
    } else {
        return {
            pkg: loc.package,
            name: loc.filename,
            exts: extensions
        }
    }
}

function deepEquals(a: any, b: any): boolean {
    const difference = diff({v:a}, {v:b});
    return Object.getOwnPropertyNames(difference).length === 0;
}


// ===== Sync Action =====

type SyncAction = {
    action: 'quality-settings',
    settings: any
} | {
    action: 'scene-create' | 'scene-delete' | 'layout' | 'clear-scene'
    sceneId: string
} | {
    action: 'bg-color'
    sceneId: string
    color?: VColor
} | {
    action: 'camera'
    sceneId: string
    camera: VCamera
} | {
    action: 'thumbnail-view'
    sceneId: string
    info?: VThumbnailInfo
} | {
    action: 'item-delete'
    sceneId: string
    itemId: string
} | {
    action: 'group-create'
    sceneId: string
    parentId?: string
    itemId: string
} | {
    action: 'light-hs' | 'directional-light' | 'shadow-catcher' | 'global-dimensions'
    sceneId: string
    on: boolean
} | {
    action: 'group-position'
    sceneId: string
    itemId: string
    position: VGroupSpatialInfo
} | {
    action: 'group-attributes'
    sceneId: string
    itemId: string
    attributes: VGroupAttributes
} | {
    action: 'part-position',
    sceneId: string
    itemId: string
    position: VPartPositioning
    rotation: VRotation
    scale: VScale
} | {
    action: 'part-animation'
    sceneId: string
    itemId: string
    percentage?: number
    fixed: boolean
} | {
    action: 'watch-nodes',
    watchedNodes: string[]
};

type AsyncAction = {
    action: 'part-create'
    sceneId: string
    parentId?: string
    part: VPart
} | {
    action: 'part-mesh-rules',
    sceneId: string
    itemId: string
    rules: VMeshRule[]
} | {
    action: 'environment'
    sceneId: string
    location?: VLocation
};

type PreloadedAction = {
    action: 'part-create-s'
    sceneId: string
    parentId?: string
    partId: string
    model: VLocationLoaded
    position: VPartPositioning
    excludeFromBoundingBox: boolean
} | {
    action: 'part-mesh-rules-s'
    sceneId: string
    itemId: string
    rules: VMeshRuleLoaded[]
} | {
    action: 'environment-s'
    sceneId: string
    location: VLocationLoaded
}

type Action = SyncAction | AsyncAction;
type ResolvedAction = SyncAction | PreloadedAction;